import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { KeyIcon, PlusIcon } from "@heroicons/react/24/outline";
import ModalRoute from "../common/ModalRoute";
import WeaveLoader from "../common/WeaveLoader";
import { useToast } from "../../contexts/ToastContext";
import { apiTokensService } from "../../services/apiTokens";
import { getApiErrorMessage } from "../../utils/axios";
import { FilterChip } from "../../pages/site/seo/shared";
import { SECTION_LABEL } from "./shared";

function formatRelativeTime(iso) {
  if (!iso) return "Never used";
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "Never used";
  const seconds = Math.round((Date.now() - then) / 1000);
  if (seconds < 60) return "Just now";
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 48) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  if (days < 60) return `${days}d ago`;
  return new Date(iso).toLocaleDateString();
}

const CreateApiKeyModal = ({ onCreated, onClose }) => {
  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [created, setCreated] = useState(null);
  const [copied, setCopied] = useState(false);

  const submit = (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    apiTokensService.createToken(
      { name: name || "Default" },
      (res) => {
        setSaving(false);
        setCreated(res.data);
      },
      (err) => {
        setSaving(false);
        setError(getApiErrorMessage(err, "Could not create API key"));
      },
    );
  };

  const copyCreated = async () => {
    if (!created?.token) return;
    try {
      await navigator.clipboard.writeText(created.token);
      setCopied(true);
    } catch (_) {}
  };

  if (created?.token) {
    return (
      <div className="p-6 space-y-4">
        <h3 className="text-lg font-semibold text-theme-text">API key created</h3>
        <p className="text-sm text-theme-text opacity-80">
          Copy this key now. It will not be shown again.
        </p>
        <code className="block text-xs font-mono break-all rounded border border-theme-secondary bg-theme-secondary bg-opacity-40 p-3 text-theme-text">
          {created.token}
        </code>
        <div className="flex gap-3 pt-2">
          <button
            type="button"
            onClick={copyCreated}
            className="flex-1 py-2 rounded-md border border-theme-secondary text-theme-text"
          >
            {copied ? "Copied" : "Copy"}
          </button>
          <button
            type="button"
            onClick={() => onCreated?.(created)}
            className="flex-1 py-2 rounded-md bg-theme-primary text-theme-button"
            style={{ backgroundColor: "var(--theme-primary)" }}
          >
            Done
          </button>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="p-6 space-y-4">
      <h3 className="text-lg font-semibold text-theme-text">Create API key</h3>
      {error && (
        <div className="rounded border border-red-500 border-opacity-40 bg-red-500 bg-opacity-10 px-3 py-2 text-sm text-theme-text">
          {error}
        </div>
      )}
      <div>
        <label className="block text-sm text-theme-text mb-1">Name</label>
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="w-full px-3 py-2 rounded-md border border-theme-secondary bg-theme-main text-theme-text"
          placeholder="Production"
        />
      </div>
      <div className="flex gap-3 pt-2">
        <button
          type="button"
          onClick={onClose}
          className="flex-1 py-2 rounded-md border border-theme-secondary text-theme-text"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={saving}
          className="flex-1 py-2 rounded-md bg-theme-primary text-theme-button disabled:opacity-50"
          style={{ backgroundColor: "var(--theme-primary)" }}
        >
          {saving ? "Creating…" : "Create"}
        </button>
      </div>
    </form>
  );
};

const ApiKeys = () => {
  const navigate = useNavigate();
  const { success: toastSuccess } = useToast();
  const [tokens, setTokens] = useState([]);
  const [showRevoked, setShowRevoked] = useState(false);
  const [selectedHash, setSelectedHash] = useState("");
  const [loading, setLoading] = useState(true);

  const refresh = ({ selectHash, quiet } = {}) => {
    if (!quiet) setLoading(true);
    apiTokensService.listTokens(
      (res) => {
        const rows = res.data?.results || res.data || [];
        const list = Array.isArray(rows) ? rows : [];
        setTokens(list);
        if (selectHash) {
          setSelectedHash(selectHash);
        } else if (!selectedHash && list[0]) {
          setSelectedHash(list[0].hash);
        }
        setLoading(false);
      },
      () => {
        setTokens([]);
        setLoading(false);
      },
    );
  };

  useEffect(() => {
    refresh();
    const onTokensChanged = () => refresh({ quiet: true });
    window.addEventListener("weave-api-tokens-changed", onTokensChanged);
    return () => window.removeEventListener("weave-api-tokens-changed", onTokensChanged);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const visible = showRevoked ? tokens : tokens.filter((t) => t.is_active);
  const selected = tokens.find((t) => t.hash === selectedHash);

  const handleCreated = (token) => {
    toastSuccess("API key was created");
    navigate("/api/keys/");
    refresh({ selectHash: token?.hash, quiet: true });
  };

  const revoke = () => {
    if (!selected || !window.confirm("Revoke this API key?")) return;
    apiTokensService.revokeToken(
      selected.hash,
      () => {
        toastSuccess("Key revoked");
        refresh({ quiet: true });
      },
      () => {},
    );
  };

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <WeaveLoader size={64} />
      </div>
    );
  }

  return (
    <div className="pb-8">
      <div className="grid grid-cols-1 gap-8 lg:grid-cols-3">
        <section className="min-w-0 lg:col-span-1">
          <div className="mb-3 flex items-center justify-between gap-2">
            <h2 className={SECTION_LABEL}>API keys</h2>
            <div className="flex items-center gap-1.5">
              <FilterChip active={showRevoked} onClick={() => setShowRevoked((v) => !v)}>
                Show revoked
              </FilterChip>
              <button
                type="button"
                onClick={() => navigate("/api/keys/new/")}
                className="inline-flex items-center gap-1 rounded-md bg-theme-primary px-2.5 py-1 text-xs font-semibold text-theme-button"
              >
                <PlusIcon className="h-3.5 w-3.5" />
                New key
              </button>
            </div>
          </div>
          {visible.length === 0 ? (
            <p className="py-10 text-center text-sm text-theme-secondary-text">No keys yet.</p>
          ) : (
            <div className="divide-y divide-theme-secondary overflow-hidden rounded-2xl border border-theme-secondary">
              {visible.map((token) => (
                <button
                  key={token.hash}
                  type="button"
                  onClick={() => setSelectedHash(token.hash)}
                  className={`flex w-full items-center gap-3 px-4 py-3 text-left transition ${
                    selectedHash === token.hash ? "bg-theme-secondary" : "seo-row-hover"
                  }`}
                >
                  <KeyIcon className="h-4 w-4 shrink-0 text-theme-secondary-text" />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-medium">{token.name || "Untitled"}</span>
                    <span className="block font-mono text-xs text-theme-secondary-text">{token.token_prefix}…</span>
                  </span>
                  <KeyStatus active={token.is_active} />
                </button>
              ))}
            </div>
          )}
        </section>

        <div className="min-w-0 lg:col-span-2">
          {selected ? (
            <div className="rounded-2xl border border-theme-secondary p-5">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <h3 className="truncate text-base font-semibold">{selected.name || "Untitled"}</h3>
                  <p className="mt-0.5 font-mono text-xs text-theme-secondary-text">{selected.token_prefix}…</p>
                </div>
                {selected.is_active ? (
                  <button
                    type="button"
                    onClick={revoke}
                    className="rounded-md bg-red-500/10 px-2.5 py-1 text-xs font-semibold text-red-500 hover:bg-red-500/15"
                  >
                    Revoke
                  </button>
                ) : null}
              </div>
              <dl className="mt-5 grid grid-cols-2 gap-4 text-sm sm:grid-cols-4">
                <div>
                  <dt className="text-xs text-theme-secondary-text">Status</dt>
                  <dd className="mt-0.5"><KeyStatus active={selected.is_active} /></dd>
                </div>
                <div>
                  <dt className="text-xs text-theme-secondary-text">Last used</dt>
                  <dd className="mt-0.5">{formatRelativeTime(selected.last_used_at)}</dd>
                </div>
                <div>
                  <dt className="text-xs text-theme-secondary-text">Created</dt>
                  <dd className="mt-0.5">{selected.create_date ? new Date(selected.create_date).toLocaleDateString() : "Unknown"}</dd>
                </div>
                <div>
                  <dt className="text-xs text-theme-secondary-text">Calls (30d)</dt>
                  <dd className="mt-0.5">{selected.request_count_30d ?? 0}</dd>
                </div>
              </dl>
            </div>
          ) : (
            <div className="mx-auto flex max-w-sm flex-col items-center py-12 text-center">
              <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-theme-secondary">
                <KeyIcon className="h-6 w-6 text-theme-primary" />
              </div>
              <h2 className="text-base font-semibold text-theme-text">No key selected</h2>
              <p className="mt-1 text-sm text-theme-secondary-text">
                Pick a key on the left or create a new one.
              </p>
            </div>
          )}
        </div>
      </div>

      <ModalRoute
        path="/api/keys/new"
        parentPath="/api/keys/"
        element={
          <CreateApiKeyModal
            onCreated={handleCreated}
            onClose={() => navigate("/api/keys/")}
          />
        }
        maxWidth="max-w-md"
        heightClass="h-auto max-h-[90vh]"
        noPadding
      />
    </div>
  );
};

function KeyStatus({ active }) {
  return (
    <span
      className={`inline-flex shrink-0 items-center gap-1.5 text-xs ${
        active ? "text-emerald-500" : "text-theme-secondary-text"
      }`}
    >
      <span className={`h-1.5 w-1.5 rounded-full ${active ? "bg-emerald-500" : "bg-theme-shadow"}`} />
      {active ? "Active" : "Revoked"}
    </span>
  );
}

export default ApiKeys;
